import moment from 'moment-timezone';

export function formatComma(value, minimumFractionDigits = 2) {
    value = value ? parseFloat(value) : 0;
    return value.toLocaleString('en-US', { minimumFractionDigits: minimumFractionDigits, maximumFractionDigits: Math.max(2, minimumFractionDigits) });
}

export const formatMinus = (val, digits = 2) => {
    const absVal = Math.abs(val);
    const formattedVal = formatComma(absVal, digits);
    return val < 0 ? `(${formattedVal})` : formattedVal;
};

export function formatNumber(value = 0, minimumFractionDigits = 2) {
    return Number(value.toFixed(minimumFractionDigits))
}


export function sum(arr = [], prop = "") {
    return arr?.reduce((accumulator, object) => {
        return accumulator + (prop ? +object[prop] : object);
    }, 0)
}



// utils.js
export const getRole = (session, role) => session && session.user?.role == role



export const findSelectedOption = (options, id) => options.find(option => option.id === id) || null;


export function convertImageToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result);
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsDataURL(file);
    });
}


export const isValidString = (value) => typeof value === 'string' && value.trim() !== '';




export const generateCloudinaryUrl = (public_id) => {
    return `https://res.cloudinary.com/dwzymxb27/image/upload/${public_id}.jpg`
}


export const formatTo12Hour = (time) => {
    // converttimer from utc to local time the time format now is 20:20
    const localTime = moment.utc(time, "HH:mm").tz(moment.tz.guess());
    return localTime.format("hh:mm A").replace("AM", "صباحًا").replace("PM", "مساءً");
};


/**
 * Checks if the current time is within the booking time slot
 * @param {string} startTime - Start time in UTC format "HH:mm"
 * @param {string} endTime - End time in UTC format "HH:mm"
 * @param {string} date - Booking date
 * @returns {boolean} - True if current time is within the booking time slot (including 1 minute before start)
 */
export const isWithinTimeSlot = (startTime = '', endTime = '', date = '') => {
    if (!startTime || !endTime || !date) return false;

    const now = moment();
    const bookingDate = moment(date);

    // Check if it's the same day
    const isSameDay = now.isSame(bookingDate, 'day');
    if (!isSameDay) return false;

    // Convert UTC times to local time
    const localStartTime = moment.utc(startTime, "HH:mm").tz(moment.tz.guess()).subtract(1, 'minute');
    const localEndTime = moment.utc(endTime, "HH:mm").tz(moment.tz.guess());

    const currentTime = now.hours() * 60 + now.minutes();
    const startTimeInMinutes = localStartTime.hours() * 60 + localStartTime.minutes();
    const endTimeInMinutes = localEndTime.hours() * 60 + localEndTime.minutes();

    return currentTime >= startTimeInMinutes && currentTime <= endTimeInMinutes;
};

/**
 * Checks if the booking slot is in the past or if current time is before start time
 * @param {string} startTime - Start time in UTC format "HH:mm"
 * @param {string} date - Booking date
 * @returns {boolean} - True if date is before today OR if it's today and current time is before start time
 */
export const isAfterSlot = (startTime = '', endTime = '', date = '') => {
    if (!startTime || !endTime || !date) return false;

    const now = moment();
    const bookingDate = moment(date);

    // If date is before today, return true
    if (bookingDate.isBefore(now, 'day')) {
        return true;
    }

    // If date is after today, return false
    if (bookingDate.isAfter(now, 'day')) {
        return false;
    }

    // For today's date, check if current time is after end time
    const localEndTime = moment.utc(endTime, "HH:mm").tz(moment.tz.guess());
    const currentTime = now.hours() * 60 + now.minutes();
    const endTimeInMinutes = localEndTime.hours() * 60 + localEndTime.minutes();

    return currentTime >= endTimeInMinutes;
};

export const isWithinTimeSlotWithPadding = (startTime = '', endTime = '', date = '') => {
    if (!startTime || !endTime || !date) return false;

    const now = moment();

    // Combine date and time then convert to local timezone
    const startDateTime = moment.utc(`${date} ${startTime}`).tz(moment.tz.guess());
    const endDateTime = moment.utc(`${date} ${endTime}`).tz(moment.tz.guess());

    // Add padding
    startDateTime.subtract(5, 'minutes');
    endDateTime.add(5, 'minutes');

    return now.isBetween(startDateTime, endDateTime);
};

export const isWithinTimeWindow = (startTime, endTime, date, startBuffer = 0, endBuffer = 0) => {
    if (!startTime || !endTime || !date) return false;

    // Ensure all moments are compared in UTC for consistency.
    const now = moment.utc();

    // The `date` is already a UTC string (e.g., "2025-07-17T00:00:00.000Z").
    // We combine it with the time parts.
    const startDateTime = moment.utc(`${moment.utc(date).format('YYYY-MM-DD')}T${startTime}`).subtract(startBuffer, 'minutes');
    const endDateTime = moment.utc(`${moment.utc(date).format('YYYY-MM-DD')}T${endTime}`).add(endBuffer, 'minutes');

    return now.isBetween(startDateTime, endDateTime);
};

export const handleBookStatus = (status) => {
    switch (status) {
        case 'reserved':
            return "تم الحجز"
        case 'completed':
            return 'مكتمل'
        case 'canceled':
            return 'تم الإلغاء'
        default:
            return 'قيد المراجعة'
    }
}

export const encode_Obj2Ref = (object) => {
    if (!object) return null;
    const encoded_Ref = btoa(JSON.stringify(object));
    return encodeURIComponent(encoded_Ref);
};

export const decode_Ref2Obj = (objRef) => {
    if (!objRef) return null;
    try {
        const decoded_Ref = JSON.parse(atob(decodeURIComponent(objRef)));
        return decoded_Ref;
    } catch (e) {
        console.error("Failed to decode schedule reference:", e);
        return null;
    }
};

export const encode_Schedule_Obj2Ref = (consultation) => {
    if (!consultation) return null;
    const { scheduleId, start, end, date, base_price, discount: discount } = consultation;
    const scheduleObj = { scheduleId, start, end, date, base_price, discount };

    return encode_Obj2Ref(scheduleObj);
};

export const decode_Schedule_Obj2Ref = (scheduleRef) => {
    if (!scheduleRef) return null;
    try {
        const scheduleObj = decode_Ref2Obj(scheduleRef);
        return scheduleObj;
    } catch (e) {
        console.error("Failed to decode schedule reference:", e);
        return null;
    }
};

